/**
 * transition-blend.util — the ONE pure module that BOTH compositors import for TRUE
 * A↔B transition blends (crossfade/dissolve, wipe, slide, push), replacing the old
 * full-frame color-flash overlays with the outgoing clip dissolving/wiping into the
 * incoming clip.
 *
 *   - preview compositor: features/super-editor/media/webav-canvas.service.ts
 *     (`drawTransitionClips`) — has `EditorClip` neighbors + a 2D canvas context.
 *   - worker compositor:  core/render-runtime/worker/worker-compositor.ts
 *     (`drawEffects` → transition family) — has `RenderClip` neighbors + an
 *     OffscreenCanvas 2D context.
 *
 * Like transform-keyframes.util / enter-exit-presets, this module is PRIMITIVE-TYPED
 * and PURE: no DOM, no Angular, no mediabunny, no canvas. The two sides carry
 * DIFFERENT clip shapes, so neighbor resolution operates on a tiny common shape
 * ({ id, startSeconds, endSeconds, kind }) each side maps its clips to, and the
 * blend operates on an abstract "draw a clip's frame into a rect" callback each side
 * supplies. The math lives here exactly once, so preview can never drift from export.
 *
 * ---------------------------------------------------------------------------
 * WHAT A TRANSITION IS (the model both sides share)
 * ---------------------------------------------------------------------------
 * A transition is a `kind:'transition'` clip dropped at the playhead on an EFFECT
 * track, spanning a window [t0,t1]. It is NOT on the visual tracks; it overlays the
 * boundary between an OUTGOING visual clip A (active/ending around the window) and an
 * INCOMING visual clip B (starting around the window) on the video/overlay tracks.
 *
 * During [t0,t1], for progress p = (t − t0) / (t1 − t0):
 *   - crossfade / dissolve: out = A·(1−p) + B·p   (alpha blend)
 *   - wipe:                 B revealed over A by a moving hard edge (directional)
 *   - slide / push:         B slides in (push also slides A out) directionally
 *
 * The genuinely STYLISTIC transitions (glitch, dip-to-black/white, flash-cut, iris,
 * blinds, pixel-blocks, zoom-hit, spin-hint) are NOT classified here as A↔B blends —
 * `classifyTransitionBlend` returns null for them and the caller keeps its existing
 * single-source-plus-color-flash overlay (no regression).
 */

/** The minimal shape of a VISUAL clip both compositors can produce for neighbor resolution. */
export interface TransitionNeighborClip {
  /** Stable clip id (used only to disambiguate A from B; never parsed). */
  id: string;
  /** Output-timeline window. */
  startSeconds: number;
  endSeconds: number;
  /** 'video' | 'image' — both are blendable; only used to keep this self-describing. */
  kind: string;
}

/** The resolved outgoing (A) / incoming (B) neighbors for a transition window. */
export interface TransitionNeighbors {
  /** Outgoing clip (active/ending at the boundary). Null when none abuts the window. */
  from: TransitionNeighborClip | null;
  /** Incoming clip (starting at the boundary), distinct from `from`. Null when none. */
  to: TransitionNeighborClip | null;
}

/** The kinds of TRUE A↔B blend this module composites (vs. stylistic color overlays). */
export type TransitionBlendKind = 'crossfade' | 'wipe' | 'slide' | 'push';

/** A fully-resolved blend plan: which two-frame composite to run, and its geometry. */
export interface TransitionBlendPlan {
  kind: TransitionBlendKind;
  /** Eased progress 0..1 across the window (already smoothed). */
  progress: number;
  /** UNSMOOTHED progress 0..1 — gl-transition shaders apply their own easing. */
  progressRaw?: number;
  /** Wipe/slide/push travel direction, as a unit vector in canvas space (x right, y down). */
  dirX: number;
  dirY: number;
  /** Optional gl-transition shader id — when set, the WebGL pass renders the blend. */
  glId?: string;
  /** Optional custom shader uniform overrides (defaults come from the bundled shader). */
  glUniforms?: Record<string, number | number[]>;
}

// ---------------------------------------------------------------------------
// 1. Classification — which transition primitives are TRUE A↔B blends
// ---------------------------------------------------------------------------

/**
 * Map a transition `primitive` (the catalog id, e.g. 'fade','wipe','slide-band') to the
 * A↔B blend it should perform, or null if it is a stylistic color overlay that must keep
 * its existing single-source behavior.
 *
 *   - 'fade'        → 'crossfade'  (the catalog's many "…Dissolve"/"Fade …" presets are
 *                                   all primitive:'fade'; a real cross-dissolve is the
 *                                   correct upgrade for ALL of them)
 *   - 'wipe'        → 'wipe'
 *   - 'slide-band'  → 'slide'      (the moving band becomes a real directional slide of B)
 *   - 'crossfade'/'dissolve' aliases → 'crossfade' (defensive; not in the catalog today)
 *
 * Everything else (glitch-bars, flash-cut, blinds, pixel-blocks, iris, zoom-hit,
 * spin-hint, dip-to-black/white when authored as a non-'fade' primitive) → null.
 *
 * NOTE on dip-to-black/white: those are authored as primitive:'fade' with a black/white
 * color in the catalog ("Fade to Black"/"Fade to White"). The task says crossfade/
 * dissolve is the priority and the stylistic dip MAY stay as a flash. We treat ALL
 * primitive:'fade' as crossfade (a true cross-dissolve reads correctly and is the
 * common case); a caller that wants to preserve a literal dip-to-color for a specific
 * preset can pass `preferColorDip` (see resolveTransitionBlendPlan).
 */
export function classifyTransitionBlend(primitive: string): TransitionBlendKind | null {
  switch (normalizeTransitionKey(primitive)) {
    case 'fade':
    case 'crossfade':
    case 'dissolve':
      return 'crossfade';
    case 'wipe':
      return 'wipe';
    case 'slide':
    case 'slide-band':
    case 'slideband':
    case 'push':
      // slide-band historically draws a passing color band; as a real A↔B blend it
      // becomes a directional slide of B over A. 'push' is reserved/forward-compatible.
      return normalizeTransitionKey(primitive) === 'push' ? 'push' : 'slide';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// 2. Neighbor resolution — find A (outgoing) and B (incoming) for a window
// ---------------------------------------------------------------------------

/** Numeric epsilon for "touches the boundary" comparisons (matches adapter rounding). */
const BOUNDARY_EPS = 0.06;

/**
 * Resolve the outgoing (A) and incoming (B) visual clips for a transition window
 * [t0,t1], given the ordered visual clips on the relevant track(s).
 *
 * DETERMINISTIC + identical for preview and worker (both pass clips in the SAME
 * bottom→top track order, array order within a track). The algorithm:
 *
 *   1. The transition's MIDPOINT tMid = (t0+t1)/2 is the conceptual cut point.
 *   2. A (outgoing) = the visual clip whose window covers t0 (the window start), i.e.
 *      it is on screen as the transition begins. If several cover t0, the LAST in the
 *      ordered list wins (topmost track / latest z) — the clip the viewer actually
 *      sees. If none covers t0, fall back to the clip ENDING nearest t0 from before.
 *   3. B (incoming) = the next visual clip in time that STARTS at/after A's start and
 *      covers t1 (on screen as the transition ends), distinct from A. Preference: the
 *      clip whose start is closest to the window (the one the cut reveals). If none
 *      covers t1, fall back to the clip STARTING nearest t1 from after.
 *
 * Both A and B may be null (e.g. a transition over a single clip, or at the very
 * start/end). The caller then has no two-frame blend to do for the missing side and
 * keeps drawing what it already composited (additive-safe).
 */
export function resolveTransitionNeighbors(
  windowStartSeconds: number,
  windowEndSeconds: number,
  clips: readonly TransitionNeighborClip[]
): TransitionNeighbors {
  const t0 = windowStartSeconds;
  const t1 = Math.max(windowStartSeconds, windowEndSeconds);
  const tMid = (t0 + t1) / 2;

  // ── A (outgoing) ──────────────────────────────────────────────────────────
  // Topmost clip on screen at the window START. Iterate forward and keep the LAST
  // match so the topmost track / latest z wins (same order both compositors pass).
  let from: TransitionNeighborClip | null = null;
  for (const c of clips) {
    if (coversTime(c, t0)) from = c;
  }
  // Fallback: nothing covers t0 → the clip that most recently ENDED before the window
  // (its end is closest to t0 from the left).
  if (!from) {
    let bestGap = Infinity;
    for (const c of clips) {
      if (c.endSeconds <= t0 + BOUNDARY_EPS) {
        const gap = t0 - c.endSeconds;
        if (gap >= -BOUNDARY_EPS && gap < bestGap) {
          bestGap = gap;
          from = c;
        }
      }
    }
  }

  // ── B (incoming) ──────────────────────────────────────────────────────────
  // The clip on screen at the window END that is DISTINCT from A and is "newer"
  // (starts at/after the cut). Prefer the one whose start is closest to the window
  // (the clip the cut reveals); among ties, the topmost (last in order).
  let to: TransitionNeighborClip | null = null;
  let bestStart = -Infinity;
  for (const c of clips) {
    if (from && c.id === from.id) continue;
    if (!coversTime(c, t1)) continue;
    // Incoming clips begin around the cut; require the start to be at/after A's start
    // (so we don't pick a clip that was already running before A). When A is null,
    // accept any clip covering t1.
    if (from && c.startSeconds < from.startSeconds - BOUNDARY_EPS) continue;
    // Closest start to the window from the left of t1 → the most recently begun clip.
    if (c.startSeconds >= bestStart) {
      bestStart = c.startSeconds;
      to = c;
    }
  }
  // Fallback: nothing distinct covers t1 → the clip that STARTS nearest after the
  // midpoint (the next clip the timeline cuts to).
  if (!to) {
    let bestGap = Infinity;
    for (const c of clips) {
      if (from && c.id === from.id) continue;
      if (c.startSeconds >= tMid - BOUNDARY_EPS) {
        const gap = c.startSeconds - tMid;
        if (gap < bestGap) {
          bestGap = gap;
          to = c;
        }
      }
    }
  }

  return { from, to };
}

/** True when clip `c`'s window covers time `t` (inclusive start, exclusive end + eps). */
function coversTime(c: TransitionNeighborClip, t: number): boolean {
  return t >= c.startSeconds - BOUNDARY_EPS && t < c.endSeconds + BOUNDARY_EPS;
}

// ---------------------------------------------------------------------------
// 3. Blend plan — progress easing + direction from the transition params
// ---------------------------------------------------------------------------

/** Optional params that shape a blend (read off the transition's params; all optional). */
export interface TransitionBlendParams {
  /** Catalog primitive id (e.g. 'fade','wipe','slide-band'). */
  primitive: string;
  /** Direction in DEGREES for wipe/slide/push (0=up, 90=right, 180=down, 270=left). */
  angle?: number;
  /** Optional gl-transition shader id — routes the blend through the WebGL pass. */
  glId?: string;
  /** Optional custom shader uniform overrides. */
  glUniforms?: Record<string, number | number[]>;
}

/**
 * Build the blend plan for a transition at raw window progress `rawProgress` (0..1),
 * or null when this primitive is NOT a true A↔B blend (the caller keeps its overlay).
 *
 * Progress is smoothed with a gentle smoothstep so the blend eases in/out (matches the
 * feel of the old sin-based fade without the dip-to-black). Direction is derived from
 * `angle` using the SAME quadrant convention the existing wipe overlay used:
 *   angle 0   → up      (reveal travels bottom→top)      dir (0,-1)
 *   angle 90  → right   (reveal travels left→right)      dir (1, 0)
 *   angle 180 → down    (reveal travels top→bottom)      dir (0, 1)
 *   angle 270 → left    (reveal travels right→left)      dir (-1,0)
 */
export function resolveTransitionBlendPlan(
  params: TransitionBlendParams,
  rawProgress: number
): TransitionBlendPlan | null {
  const kind = classifyTransitionBlend(params.primitive);
  if (!kind) return null;
  const raw = clamp01(rawProgress);
  const progress = smoothstep(raw);
  const { dirX, dirY } = directionFromAngle(params.angle ?? 90);
  const plan: TransitionBlendPlan = { kind, progress, progressRaw: raw, dirX, dirY };
  if (params.glId) {
    plan.glId = params.glId;
    if (params.glUniforms) plan.glUniforms = params.glUniforms;
  }
  return plan;
}

/**
 * Map a wipe/slide angle (degrees) to a unit direction in canvas space (x right, y
 * down). Mirrors the quadrant buckets the old `wipe` overlay used so the upgraded blend
 * reveals in the SAME direction the preset always implied.
 */
function directionFromAngle(angle: number): { dirX: number; dirY: number } {
  const a = ((angle % 360) + 360) % 360;
  if (a >= 45 && a < 135) return { dirX: 1, dirY: 0 };    // right
  if (a >= 135 && a < 225) return { dirX: 0, dirY: 1 };   // down
  if (a >= 225 && a < 315) return { dirX: -1, dirY: 0 };  // left
  return { dirX: 0, dirY: -1 };                            // up
}

// ---------------------------------------------------------------------------
// 4. Blend geometry — pure helpers each compositor uses to drive its 2D context
// ---------------------------------------------------------------------------

/**
 * For a WIPE: the axis-aligned rectangle of B that is revealed over A at this progress,
 * in PIXELS, given the canvas size and the blend direction. B is drawn fully, then
 * clipped to this rect; A shows everywhere else. (The compositor draws A first, then B
 * clipped to this rect on top.)
 *
 * The revealed region grows from the side the direction points AWAY from, toward the
 * side it points to — e.g. dir (1,0) "right" reveals B from the LEFT edge growing
 * rightward, so at p the left `p·W` is B.
 */
export function wipeRevealRect(
  plan: TransitionBlendPlan,
  width: number,
  height: number
): { x: number; y: number; w: number; h: number } {
  const p = plan.progress;
  if (plan.dirX > 0) return { x: 0, y: 0, w: width * p, h: height };          // reveal from left
  if (plan.dirX < 0) return { x: width * (1 - p), y: 0, w: width * p, h: height }; // from right
  if (plan.dirY > 0) return { x: 0, y: 0, w: width, h: height * p };          // from top
  return { x: 0, y: height * (1 - p), w: width, h: height * p };             // from bottom (dir up)
}

/**
 * For a SLIDE/PUSH: the pixel offset (dx,dy) by which the INCOMING clip B is shifted so
 * it slides in along the direction. At p=0 B is fully off-screen on the side it enters
 * from; at p=1 it sits at the origin (covering the frame).
 *
 * For PUSH the OUTGOING clip A is simultaneously shifted OUT by the complementary offset
 * (see {@link slideOutgoingOffset}); for SLIDE A stays put and B slides over it.
 */
export function slideIncomingOffset(
  plan: TransitionBlendPlan,
  width: number,
  height: number
): { dx: number; dy: number } {
  const off = 1 - plan.progress; // 1 → fully off-screen, 0 → settled
  // B enters from the side the reveal travels FROM = opposite the direction vector.
  return { dx: -plan.dirX * width * off, dy: -plan.dirY * height * off };
}

/** For PUSH: the pixel offset by which the OUTGOING clip A is pushed off-screen. */
export function slideOutgoingOffset(
  plan: TransitionBlendPlan,
  width: number,
  height: number
): { dx: number; dy: number } {
  const off = plan.progress; // 0 → in place, 1 → fully pushed out along the direction
  return { dx: plan.dirX * width * off, dy: plan.dirY * height * off };
}

/**
 * Crossfade alphas. The compositors draw A FIRST at {@link crossfadeFromAlpha} (opaque,
 * = 1) onto a clean buffer, then B OVER it at {@link crossfadeToAlpha} (= p). Canvas
 * source-over then yields exactly `out = A·(1−p) + B·p` as an OPAQUE pixel in the region
 * both cover — the textbook two-image crossfade. (Drawing A at 1−p instead would leave the
 * buffer semi-transparent and the math would depend on whatever is underneath.)
 *
 * When only ONE side is present (a transition at the very start/end, or a lone clip), that
 * side is still drawn at its alpha: a missing A means B fades UP from transparent over the
 * already-composited frame (B at p), and a missing B means A is drawn opaque (A at 1) — both
 * sensible, and additive-safe.
 */
export function crossfadeFromAlpha(_plan: TransitionBlendPlan): number {
  return 1;
}

/** The B (incoming) global alpha for a crossfade: 0 → 1 across the window. */
export function crossfadeToAlpha(plan: TransitionBlendPlan): number {
  return clamp01(plan.progress);
}

// ---------------------------------------------------------------------------
// Small numeric helpers
// ---------------------------------------------------------------------------

/** Lower-cases and strips spaces/dashes/underscores so 'slide-band' === 'slide_band'. */
export function normalizeTransitionKey(value: string): string {
  return String(value || '').trim().toLowerCase().replace(/[\s_]+/g, '-');
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Smoothstep ease (3t²−2t³) — gentle in/out, no overshoot. */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}
